import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useDispatch } from "react-redux"
import { debounce } from "lodash"
import { getPostTripPreview } from "store/bookings"
import { buildAddOnSelectionsAttributes } from "components/bookings/helpers"

// The operator types into a quantity stepper; without this every keystroke is a round trip.
const PREVIEW_DEBOUNCE_MS = 400

/**
 * DS-4473. Prices a completed booking's edit on the backend instead of in the browser.
 *
 * A sold booking is priced against a frozen snapshot only the backend holds, so `calculateBookingTotal`
 * — which reads the experience's *current* day/seasonal adjustments and rates — re-prices a trip that
 * already ran and offers a refund nobody is owed. This asks the backend what the save would produce.
 *
 * Requests only once there is a change to price. With no add-on edits pending there is nothing to
 * preview — the booking's stored payment_summary is already what the client agreed to, and it is
 * what the modal shows.
 *
 * @param {Object}   params
 * @param {String}   params.bookingId   Booking id/uuid — the same one PUT /guide/bookings/:id takes.
 * @param {Object}   params.addOns      Current add-on selections, keyed by add-on id.
 * @param {Array}    params.existingBookingAddOns  The booking's saved add-on rows, so quantities are
 *                                      sent as absolute totals (existing + new) per the API contract.
 * @param {Boolean}  params.isActive    Only true in post-trip mode; nothing is requested otherwise.
 * @returns {{ preview: Object|null, isLoading: Boolean, error: Object|null, hasPendingChanges: Boolean, refresh: Function }}
 */
export const usePostTripPreview = ({ bookingId, addOns, existingBookingAddOns, isActive = false }) => {
  const dispatch = useDispatch()
  const [preview, setPreview] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  // Debounced requests can resolve out of order; only the newest may write. Without this a slow
  // earlier response lands last and shows a price for a quantity the operator has moved past.
  const latestRequestRef = useRef(0)

  const addOnsAttributes = useMemo(
    () => buildAddOnSelectionsAttributes(addOns || {}, existingBookingAddOns || []),
    [addOns, existingBookingAddOns]
  )
  // Serialized so the effect below re-runs on a real change of quantities, not on every render that
  // rebuilds an equivalent array.
  const addOnsKey = JSON.stringify(addOnsAttributes)

  const fetchPreview = useCallback(
    async (attributes) => {
      // Nothing to price. The endpoint rejects empty line_items (422, "At least one add-on is
      // required"), and rightly so: with no change pending, the booking's own stored payment_summary
      // is already the authoritative answer and there is nothing to preview.
      if (!bookingId || !attributes?.length) return
      const requestId = latestRequestRef.current + 1
      latestRequestRef.current = requestId
      setIsLoading(true)
      try {
        const response = await dispatch(getPostTripPreview(bookingId, attributes))
        if (latestRequestRef.current !== requestId) return
        setPreview(response?.data ?? null)
        setError(null)
      } catch (err) {
        if (latestRequestRef.current !== requestId) return
        // The previous numbers are left on screen deliberately: they came from the backend too, and
        // blanking the price block on a transient failure is worse than showing a slightly old one.
        setError(err)
      } finally {
        if (latestRequestRef.current === requestId) setIsLoading(false)
      }
    },
    [dispatch, bookingId]
  )

  const debouncedFetch = useMemo(() => debounce(fetchPreview, PREVIEW_DEBOUNCE_MS), [fetchPreview])
  useEffect(() => () => debouncedFetch.cancel(), [debouncedFetch])

  useEffect(() => {
    if (!isActive || !bookingId) return
    const attributes = JSON.parse(addOnsKey)
    // Back to no pending change (every added add-on removed again): drop the last preview, or its
    // total would keep standing in for a booking that is no longer being changed. Cancel first, so
    // an in-flight debounce can't repopulate what was just cleared.
    if (!attributes.length) {
      debouncedFetch.cancel()
      latestRequestRef.current += 1
      setPreview(null)
      setError(null)
      setIsLoading(false)
      return
    }
    debouncedFetch(attributes)
  }, [isActive, bookingId, addOnsKey, debouncedFetch])

  // Called after a 409 on save: re-prices against the booking as it now stands and hands back a
  // fresh updated_at to pin the next attempt to.
  const refresh = useCallback(() => {
    debouncedFetch.cancel()
    return fetchPreview(addOnsAttributes)
  }, [debouncedFetch, fetchPreview, addOnsAttributes])

  // Distinguishes "no price to show because nothing changed" from "no price to show because the
  // backend hasn't given us one" — only the second is a problem worth telling the operator about.
  return { preview, isLoading, error, hasPendingChanges: addOnsAttributes.length > 0, refresh }
}
